import type { UploadObjectControlRow } from "./uploadKnownSizeControl";
import type { LostRecoveryPolicy } from "./completionTransition";
import type { GatedOutcome } from "./ownershipGate";
import type {
	ListPartsEvidence,
	ReconciliationPart,
	ReconciliationDataClassification,
	ReconciliationTerminalPolicy
} from "./reconciliationStateMachine";
import type {
	FamilyAwareMultipartUploadTarget,
	MultipartUploadTarget,
	UploadCompletionPart,
	UploadEndpointFamily,
	UploadFileLike,
	UploadTargetEnvelope,
	UploadsCompletionPart,
	UploadsMultipartUploadTarget
} from "./targetInitiation";
import type { ChunkSource, ChunkSourceFailureKind, S3ChunkTransport } from "./uploadChunk";
import type { SourceReadStrategy } from "./sourceReadStrategy";
import type {
	DurableLeaseRecord,
	DurableItemIdentity,
	DurableLeaseToken,
	DurablePoisonRecord,
	DurableUploadStoreName,
	DurableWorkRecord
} from "./durableUploadSchema";

export type VerifyReinitializableResult = "guaranteed" | "missing" | "indeterminate";
export type OwnershipWriteOutcome = "applied" | "skipped-not-owned";
/** See SessionStore.markNeedsAttention — its CAS declines for several materially different reasons. */
export type NeedsAttentionOutcome = GatedOutcome | "already-complete" | "already-bytes-gone" | "already-cancelled";
export type MultipartUrlRefreshResult = {
	partNumber: number;
	url: string;
	envelope?: UploadTargetEnvelope;
};

/**
 * A routine refresh returns only `updates`. An authenticated uploads-facade response that moved the
 * session to a new attempt additionally returns the complete replacement target. Keeping the signal
 * outside each envelope distinguishes an intentional session migration from one corrupted envelope.
 */
export type MultipartUrlRefreshBatch = MultipartUrlRefreshResult[] & {
	replacementTarget?: UploadsMultipartUploadTarget;
};

/** Old-to-new fence metadata used by durable stores to CAS an attempt migration atomically. */
export type MultipartUrlRefreshTargetMigration = {
	previousAttemptId: string;
	previousGeneration: number;
	replacementTarget: UploadsMultipartUploadTarget;
};

export type TargetAttachmentResult<Session> = { kind: "attached"; session: Session } | { kind: "skipped-not-owned"; session: Session };

export type MultipartTargetValidationFailure = {
	ok: false;
	reason:
		| "empty-file"
		| "too-many-parts"
		| "too-few-urls"
		| "extra-urls"
		| "invalid-urls"
		| "invalid-upload-id"
		| "invalid-s3-key"
		| "invalid-bucket";
	requiredChunks: number;
	targetChunkCount: number;
};

/**
 * Persistence boundary expressed in upload-domain operations. Adapters map
 * these semantics to their app-specific status names and atomic transactions.
 */
export interface SessionStore<Session, ChunkMeta = unknown, ChunkData = Blob> {
	releasePending(session: Session, reason?: string, options?: { retryAt?: number | null; retryAttempt?: number }): Promise<void>;
	recordFailure?(session: Session, reason: string): Promise<void>;
	markTerminalComplete(session: Session): Promise<void>;
	markBytesGone(session: Session, reason?: string): Promise<void>;
	/**
	 * Returns "skipped-not-owned" when the adapter's own durable CAS was declined because a DIFFERENT
	 * upload identity owns the row now (which may not be terminal at all) — the caller must then treat
	 * the session exactly as it treats a lost lease, never as "durably marked". Returns
	 * "already-complete"/"already-bytes-gone"/"already-cancelled" when the CAS was declined because a MORE authoritative
	 * terminal verdict legitimately won the race: nothing is wrong, but the row's real terminal state
	 * is that winning status, NOT needs-attention, and a caller that reports it as needs-attention
	 * misdescribes a session that actually finished (or is confirmed byte-less). Returning nothing
	 * means the needs-attention verdict itself is durable (PR #3450 review / LSP-17735).
	 */
	markNeedsAttention(session: Session, reason?: string, failure?: unknown): Promise<NeedsAttentionOutcome | void>;
	tombstoneCancelled(session: Session, options?: { notifyOtherTabs?: boolean }): Promise<void>;
	listChunkMeta(session: Session): Promise<ChunkMeta[]>;
	putChunkMeta(session: Session, chunk: ChunkMeta, options?: { ifAbsent?: boolean }): Promise<OwnershipWriteOutcome>;
	readChunkData(session: Session, chunkIndex: number): Promise<ChunkData | undefined>;
	verifyReinitializable(session: Session): Promise<VerifyReinitializableResult>;
	/**
	 * Chunked-object completion refused with the ordinals still owed: flip exactly those chunks back
	 * to `isUploaded: false, etag: null` so the ordinary chunk loop re-sends them. Optional because the
	 * legacy multipart lane never receives that refusal.
	 *
	 * Returns the number of chunk rows the write actually modified. A `0` (the CAS refused: a stale
	 * owner claim, a re-minted upload id, a row already terminal) means the next `/complete` body is
	 * byte-identical to the one just refused, which is what the parts-incomplete bound counts as
	 * unproductive — so a `void` return, which is what both stores used to give, hid a refused write
	 * behind "re-marked". `void` remains accepted for implementations that genuinely cannot count.
	 */
	markChunksNotUploaded?(session: Session, chunkIndexes: readonly number[]): Promise<number | void>;
}

export type OwnerLiveness = "alive" | "dead" | "indeterminate";

/**
 * Ownership boundary implemented by Web Locks in admin and heartbeat-CAS in
 * async. The engine consumes only the shared liveness semantics.
 */
export interface UploadLease<Session> {
	claim(session: Session): Promise<boolean>;
	touch(session: Session): Promise<void>;
	owns(session: Session): Promise<boolean>;
	ownerLiveness(session: Session): Promise<OwnerLiveness>;
	/**
	 * Optional P-8 writer fence. Implementations run the effect inside the
	 * same application IndexedDB transaction that validates owner, epoch, and
	 * immutable item identity. A scheduler lease is intentionally insufficient.
	 */
	commitIfOwned?<T>(session: Session, effect: () => Promise<T>): Promise<{ kind: "applied"; value: T } | { kind: "skipped-not-owned" }>;
}

/** A transaction view exposed to explicitly registered app-owned stores. */
export interface DurableUploadTransaction {
	get<Row>(storeName: DurableUploadStoreName, key: IDBValidKey): Promise<Row | undefined>;
	put<Row>(storeName: DurableUploadStoreName, value: Row, key?: IDBValidKey): Promise<void>;
	delete(storeName: DurableUploadStoreName, key: IDBValidKey): Promise<void>;
}

export type DurableOwnershipWriteOutcome<T> = { kind: "applied"; value: T } | { kind: "skipped-not-owned" };

/**
 * LSP-23117: optional liveness fields on a lease acquisition. `livenessLock` is stamped on the row this
 * owner writes. `replaceOwner` lets the caller take over an UNEXPIRED lease, but only one whose row still
 * names exactly that owner and epoch and records a liveness lock; the caller must hold that (free) lock
 * for the whole acquisition so the old owner cannot come back mid-handover. Callers that pass neither
 * field get the TTL-only behaviour unchanged.
 *
 * A same-owner re-acquisition writes `livenessLock` exactly as passed: an owner that does not hold its lock
 * right now (grant timed out, or not yet re-granted after `pageshow`) clears the stamp, so a successor cannot
 * prove a live owner gone from a free lock; its lease falls back to the TTL until the next stamped write.
 */
export interface DurableLeaseLiveness {
	livenessLock?: string;
	/**
	 * Reads the stamp instead of `livenessLock`, INSIDE the acquiring transaction, right before the row is built. A
	 * value captured before an await can be stale by commit time (a page restored by `pageshow` whose lock is not yet
	 * re-granted would stamp a lock it does not hold), so callers whose lock state changes use this.
	 */
	readLivenessLock?: () => string | undefined;
	replaceOwner?: { ownerId: string; epoch: number };
}

/**
 * LSP-23117: a renewal from a caller that stamps liveness locks. Present: the row's stamp becomes `livenessLock`
 * (cleared when undefined, by the same rule as a same-owner re-acquisition). Absent: the stamp is left as it is,
 * for callers that never stamp.
 */
export interface DurableLeaseRenewalLiveness {
	/** `readLivenessLock`, when given, is read inside the renewing transaction, as on an acquisition. */
	liveness?: { livenessLock?: string; readLivenessLock?: () => string | undefined };
}

/**
 * Durable browser ownership contract. Web Locks and BroadcastChannel may wrap
 * these calls, but never replace them as the correctness primitive.
 */
export interface DurableOwnershipPort {
	acquireScheduler(
		input: {
			originKey: string;
			ownerId: string;
			identity?: DurableItemIdentity;
			ttlMs: number;
			now?: number;
		} & DurableLeaseLiveness
	): Promise<DurableLeaseToken | undefined>;
	/** Extends an unexpired scheduler epoch without allowing an expired/stolen owner to return. */
	renewScheduler?(
		input: { token: DurableLeaseToken; ttlMs: number; now?: number } & DurableLeaseRenewalLiveness
	): Promise<DurableLeaseToken | undefined>;
	releaseScheduler(input: {
		token: DurableLeaseToken;
		isIdle: () => Promise<boolean>;
		now?: number;
	}): Promise<DurableOwnershipWriteOutcome<void>>;
	/**
	 * Give up a scheduler lease this owner still holds, WITHOUT requiring the origin to be idle.
	 *
	 * `releaseScheduler` above is the normal path and is deliberately gated on idleness: a leader that
	 * still has pending work keeps the lease so it can carry on draining instead of re-contending every
	 * pass. That gate has a cost the drain has to pay for separately — a page that goes away mid-work
	 * leaves the lease held for its whole TTL, and the NEXT page load then yields ("another tab owns
	 * the scheduler") even though nothing is running. With uploads pending, which is exactly when a
	 * user reloads to make them go, that is a guaranteed stall of up to the TTL.
	 *
	 * This is the shutdown/handover counterpart: the caller is stating that this owner cannot continue,
	 * so holding the lease protects nothing. It is still fenced on OWNER IDENTITY — it can only clear a
	 * lease whose `ownerId` matches, never another tab's — so it is a handover, not a steal, and it
	 * makes no liveness judgement about anyone else.
	 */
	abandonScheduler?(input: { originKey: string; ownerId: string; now?: number }): Promise<DurableOwnershipWriteOutcome<void>>;
	/**
	 * Diagnostic read of the scheduler lease row, decayed or not. Exists so a drain that could not
	 * acquire the scheduler can say WHO holds it and for how much longer, instead of "another tab owns
	 * it" with no state — the line that hid the LSP-19088 orphaned-lease stall. Never a decision input:
	 * an LSP-23117 takeover uses it only to name the holder, and `acquireScheduler`'s `replaceOwner`
	 * compare-and-swap re-checks that holder inside the acquiring transaction.
	 */
	readSchedulerLease?(originKey: string): Promise<DurableLeaseRecord | undefined>;
	noteWork(input: { originKey: string; pendingCount?: number; hasDueWork?: boolean; now?: number }): Promise<DurableWorkRecord>;
	readWork(originKey: string): Promise<DurableWorkRecord | undefined>;

	acquireWriter(
		input: {
			originKey: string;
			identity: DurableItemIdentity;
			ownerId: string;
			ttlMs: number;
			now?: number;
		} & DurableLeaseLiveness
	): Promise<DurableLeaseToken | undefined>;
	/** LSP-23117: read of the writer lease row, so a refused claim can find the holder's liveness lock. */
	readWriterLease?(originKey: string, identity: DurableItemIdentity): Promise<DurableLeaseRecord | undefined>;
	renewWriter(
		input: { token: DurableLeaseToken; ttlMs: number; now?: number } & DurableLeaseRenewalLiveness
	): Promise<DurableLeaseToken | undefined>;
	releaseWriter(token: DurableLeaseToken, now?: number): Promise<DurableOwnershipWriteOutcome<void>>;
	ownsWriter(token: DurableLeaseToken, now?: number): Promise<boolean>;
	commitIfWriterOwns<T>(input: {
		token: DurableLeaseToken;
		storeNames?: readonly DurableUploadStoreName[];
		now?: number;
		effect: (transaction?: DurableUploadTransaction) => Promise<T> | T;
	}): Promise<DurableOwnershipWriteOutcome<T>>;

	recordPoisonFailure(input: {
		originKey: string;
		identity: DurableItemIdentity;
		reason: string;
		ceiling: number;
		now?: number;
		/** Must be the transaction supplied by commitIfWriterOwns for production mutations. */
		transaction?: DurableUploadTransaction;
	}): Promise<DurablePoisonRecord>;
	readPoisonFailure(originKey: string, identity: DurableItemIdentity): Promise<DurablePoisonRecord | undefined>;
	isPoisoned(originKey: string, identity: DurableItemIdentity): Promise<boolean>;
	/**
	 * Clears the consecutive poison budget after a successful item completion.
	 * When supplied, the transaction must be the writer-fenced commit transaction.
	 */
	clearPoisonFailure?(originKey: string, identity: DurableItemIdentity, transaction?: DurableUploadTransaction): Promise<void>;
}

export interface UploadTelemetry {
	logInfo(event: string, ctx?: unknown, error?: unknown): void;
	logWarn(event: string, ctx?: unknown, error?: unknown): void;
	logError(event: string, ctx?: unknown, error?: unknown): void;
}

export type UploadFileCategory = "image" | "document" | "video";

export interface DriverFile extends UploadFileLike {
	slice(start?: number, end?: number): Blob;
	arrayBuffer(): Promise<ArrayBuffer>;
	/** Optional forward-only source used for large, non-seekable browser handles. */
	stream?: () => ReadableStream<Uint8Array>;
}

export interface MultipartSessionShape extends UploadObjectControlRow {
	id: string;
	/** Same-tab session that intentionally has no durable persistence or owner. */
	ephemeral?: boolean;
	conversationId: string;
	fileName: string;
	fileSize: number;
	fileType: string;
	fileCategory: UploadFileCategory;
	uploadId: string;
	s3Key: string;
	bucket: string;
	totalChunks: number;
	chunkUrlsJson: string;
	urlCreatedAt: number;
	createdAt: number;
	cloudHosted: boolean;
	/** Endpoint family is immutable once a session is admitted; missing pre-marker rows are legacy. */
	endpointFamily?: UploadEndpointFamily;
	/** Opaque `/uploads` registry target identity; no storage coordinates. */
	registryId?: string;
	attemptId?: string;
	generation?: number;
	takeGeneration?: string;
	targetEnvelopesJson?: string;
	/**
	 * The transport the `/uploads` facade minted for this attempt. Absent on every row persisted
	 * before plan 3a, which read as "multipart" — see `uploadsTargetTransport`.
	 */
	transport?: "multipart" | "chunked-object";
	/** Chunk size the grid was built with; persisted so a resume cannot re-slice on a different grid. */
	chunkSizeBytes?: number;
	/** JSON array of 0-based chunk indexes in upload priority order (headerFirstChunkOrder). Absent = ascending. */
	priorityChunkIndexesJson?: string;
	/** Strategy selected while the source was first persisted; retained for resume provenance. */
	sourceReadStrategy?: SourceReadStrategy;
	/** Durable retry deadline. `undefined` means no scheduled retry is pending. */
	retryAt?: number | null;
	retryAttempt?: number;
	/** Server-confirmed identity to prefer for persisted target reinitiation. */
	reconciledIdempotencyId?: string;
	idempotencyId?: string;
	fileExtension?: string;
	projectChecklistItemId?: string;
	photoTime?: string;
	captureEvidence?: Record<string, unknown>;
}

export interface DriverChunkMeta {
	chunkIndex: number;
	size: number;
	isUploaded: boolean;
	etag: string | null;
}

export interface NewSessionInput {
	id: string;
	/** Durable grouping for files selected in one user upload action. */
	batchId?: string;
	uploadScope?: { organizationId?: string; userId?: string };
	/** Create an in-memory-only session with no persistence or real lease. */
	ephemeral?: boolean;
	conversationId: string;
	fileName: string;
	fileSize: number;
	fileType: string;
	fileCategory: UploadFileCategory;
	target?: FamilyAwareMultipartUploadTarget;
	totalChunks: number;
	cloudHosted: boolean;
	/** New sessions default to legacy until a lane explicitly opts into `/uploads`. */
	endpointFamily?: UploadEndpointFamily;
	sourceReadStrategy?: SourceReadStrategy;
	phase: "pre-persist" | "deferred" | "direct-upload";
	stableId: string;
	projectChecklistItemId?: string;
	photoTime?: string;
	captureEvidence?: Record<string, unknown>;
	/** See ChunkedUploadDriver.persistFileToIndexedDB's identical parameter (LSP-17735). */
	detectedMimeType?: string;
}

/**
 * `needs-attention` is a TERMINAL refusal the caller must not retry and must not treat as a generic
 * failure: the driver already marked the persisted session needs-attention (with its reason) before
 * returning, so the consumer's job is only to surface that state. It is deliberately distinct from
 * `retry` — a persisted-byte-total contradiction is immutable, so any caller inspecting the bare
 * outcome must be able to tell it apart from a genuinely retryable one (LSP-17735).
 */
export type DriverResumeOutcome =
	| "completed"
	| "retry"
	| "lost"
	| "handed-off"
	| "bytes-gone"
	| "yielded"
	| "needs-attention"
	| "recovery-blocked";
export type DriverUploadOutcome = "completed" | "yielded";
export type DeferredTargetAttachmentOutcome = "attached" | "yielded";

export type ResumeFailureKind =
	| "retry"
	| "retry-slow"
	| "lost"
	| "terminal-read-failure"
	| "terminal-local-gaps"
	| "needs-attention"
	| "recover-lost"
	| "persistence-failure"
	| "other";

interface PersistedResumePreparedUpload {
	kind: "ready";
	chunkSource?: ChunkSource;
	onChunkComplete?: (input: { chunkIndex: number; chunkSize: number; chunksCompleted: number; totalPendingChunks: number }) => void;
	createTruncatedPartsError?: (uploadedPartCount: number) => Error;
}

interface PersistedResumePort<Session extends MultipartSessionShape, FailureKind extends ResumeFailureKind = ResumeFailureKind> {
	classifyData(data: ArrayBuffer | undefined, chunk: DriverChunkMeta): ReconciliationDataClassification;
	readonly reconcileReadFailure?: boolean | ((error: unknown) => boolean);
	readonly reconciliationDefer: "retry" | "throw";
	prepareUpload(input: {
		session: Session;
		sourceReadStrategy?: SourceReadStrategy;
		allChunkMeta: DriverChunkMeta[];
		unuploadedMeta: DriverChunkMeta[];
	}): Promise<PersistedResumePreparedUpload | { kind: "retry" }>;
	classifyFailure(error: unknown): FailureKind;
	createNonContiguousPartsError?(input: { session: Session; uploadedPartCount: number }): Error;
}

interface ChunkedUploadSessionStore<Session extends MultipartSessionShape, LeaseToken = unknown> extends SessionStore<
	Session,
	DriverChunkMeta,
	ArrayBuffer
> {
	getById(sessionId: string): Promise<Session | undefined>;
	findByUploadId(uploadId: string): Promise<Session | undefined>;
	createSession(input: NewSessionInput, leaseToken: LeaseToken): Promise<{ session: Session; created: boolean }>;
	persistChunk(session: Session, chunk: { chunkIndex: number; size: number; data: ArrayBuffer }): Promise<void>;
	finishPrePersist(session: Session): Promise<void>;
	markChunkUploaded(session: Session, chunkIndex: number, etag: string): Promise<void>;
	abandonSetup(session: Session): Promise<void>;
	deleteAllChunkData(session: Session): Promise<void>;
	cleanupCompleted(session: Session): Promise<void>;
	discardIfUploadIdMatches(sessionId: string, uploadId: string): Promise<void>;
	mergeAndPersistUrls(
		session: Session,
		updates: readonly MultipartUrlRefreshResult[],
		fallbackUrls: readonly string[],
		refreshesAllPendingUrls?: boolean,
		targetMigration?: MultipartUrlRefreshTargetMigration
	): Promise<string[]>;
	attachTarget(
		session: Session,
		target: FamilyAwareMultipartUploadTarget,
		leaseToken: LeaseToken,
		retainLease?: boolean
	): Promise<Session | TargetAttachmentResult<Session>>;
	resetForReinitialization(session: Session, target: FamilyAwareMultipartUploadTarget): Promise<Session>;
}

type UploadLeasePurpose = "pre-persist" | "direct-upload" | "resume";

export interface UploadLeaseScope<Session, LeaseToken = unknown> {
	readonly token: LeaseToken;
	readonly lease: UploadLease<Session>;
	activate(session: Session): void;
	runWithLiveness<T>(session: Session, work: () => Promise<T>): Promise<T>;
	/**
	 * Gives up this scope's claim on `session`.
	 *
	 * An implementation MUST retire every per-app protection its own `claim()` created for this row —
	 * not only the in-memory lease entry. Anything a claim switched on that makes a recovery sweep or
	 * a drain treat the row as "someone is driving this, leave it alone" has to switch back off here,
	 * because after this call nothing in this scope is driving it.
	 *
	 * This is not hypothetical tidiness. contact-admin's `claim()` marks a row as
	 * current-page-admitted, which makes its orphan sweep protect the row unconditionally — ahead of
	 * every liveness signal, heartbeat included (LSP-17574). That mark was cleared only by a durable
	 * status write, and a lane that gives up mid-flight makes none: every demote in this driver runs
	 * through `runGuardedEffect`, and `owns()` can answer false with NO successor at all (a lost or
	 * expired durable writer token short-circuits it while the row's owner columns still name this
	 * claim). The mark therefore outlived the claim, the sweep kept skipping the row for the life of
	 * the page, and admin's drain — which reads only `status: "pending"` — could never see it either.
	 * Uploading a batch stranded one row per lost lease until the dialog was stuck "uploading".
	 *
	 * Two rules this contract does NOT impose, both deliberate:
	 *  - Do not demote the row here. A cancelled upload must stay out of the drainable pool
	 *    (contact-admin LSP-15669). Retiring the protection is enough; the app's ordinary liveness
	 *    rules then apply to the row like any other.
	 *  - Do not condition the retirement on a durable token still being held. That token is already
	 *    gone on the path that matters most — a failed writer renewal drops it — so gating on it makes
	 *    the retirement dead on exactly the failure it exists to compensate.
	 */
	release(session: Session): void;
	/**
	 * Snapshots this scope's in-memory claim generation for `session` and returns a predicate that
	 * reports whether that generation is STILL the live one.
	 *
	 * A lease token is reused across claims for a given session (async's coordinator resolves it as
	 * `requestedToken ?? <tracked token> ?? <fresh token>`), so token equality alone cannot tell an
	 * older, superseded claim apart from the newer one that replaced it — only the generation can.
	 * `owns()` already compares generations internally; this exposes the same signal to the few
	 * compensation paths that deliberately cannot call `owns()` (see
	 * chunkedUploadDriver's demoteAbandonedDirectClaimBestEffort, whose gate would re-issue the very
	 * read that just failed). Optional: an implementation with no generation concept simply omits
	 * it, and those paths fall back to their durable CAS alone (PR #3450 review / LSP-17735).
	 */
	captureClaimGeneration?(session: Session): () => boolean;
}

interface UploadLeaseCoordinator<Session, LeaseToken = unknown> {
	createScope(purpose: UploadLeasePurpose, sessionId?: string, requestedToken?: LeaseToken): UploadLeaseScope<Session, LeaseToken>;
}

export interface ChunkSourceFactory<FileLike extends DriverFile, Session extends MultipartSessionShape> {
	detectCloudHosted(file: FileLike, context?: { sessionId: string; phase: string }): Promise<boolean>;
	readWholeFile(file: FileLike, signal: AbortSignal): Promise<ArrayBuffer>;
	forPersistence(file: FileLike, wholeFile?: ArrayBuffer): PersistableChunkSource;
	forDirectUpload(input: { file: FileLike; session?: Session; cloudHosted: boolean; chunkSize: number }): ChunkSource;
	forPersistedUpload(session: Session): ChunkSource;
}

export interface PersistableChunkSource extends ChunkSource {
	readBuffer(
		index: number,
		signal: AbortSignal
	): Promise<{ ok: true; buffer: ArrayBuffer } | { ok: false; kind: ChunkSourceFailureKind }>;
}

export interface MultipartApiPort<Session extends MultipartSessionShape> {
	partReceipt?(target: Session, ordinal: number): Promise<ReconciliationPart | null>;
	progress?(
		target: Pick<
			Session,
			| "uploadId"
			| "s3Key"
			| "bucket"
			| "endpointFamily"
			| "registryId"
			| "attemptId"
			| "generation"
			| "targetEnvelopesJson"
			| "transport"
			| "takeGeneration"
			| "contractVersion"
			| "objectUploadJson"
			| "protocolSelectionPending"
		>,
		progress: {
			receivedParts?: number;
			receivedBytes?: number;
			receivedMaxChunkIndex?: number;
		},
		options?: { signal?: AbortSignal }
	): Promise<void>;
	complete(
		target: Pick<
			Session,
			| "uploadId"
			| "s3Key"
			| "bucket"
			| "endpointFamily"
			| "registryId"
			| "attemptId"
			| "generation"
			| "targetEnvelopesJson"
			| "transport"
			| "takeGeneration"
			| "contractVersion"
			| "objectUploadJson"
			| "protocolSelectionPending"
		>,
		parts: readonly { ETag: string; PartNumber: number }[]
	): Promise<{ alreadyComplete: boolean }>;
	listParts(
		target: Session,
		persistenceFence?: {
			lease: UploadLease<Session>;
			ownsCurrentSession: () => Promise<boolean>;
		}
	): Promise<ListPartsEvidence>;
	/**
	 * LSP-23082: the `/uploads` facade's unfenced receipt (`GET /uploads/:id`) — the server's current attempt and
	 * status for this upload. Read only to decide what a refused URL refresh means (`refusedAttemptProbe.ts`). Optional:
	 * a host without it keeps its existing classification of that refusal.
	 */
	readReceipt?(target: { registryId: string }): Promise<unknown>;
	refreshUrls(
		target: Pick<
			Session,
			| "uploadId"
			| "s3Key"
			| "bucket"
			| "endpointFamily"
			| "registryId"
			| "attemptId"
			| "generation"
			| "takeGeneration"
			| "targetEnvelopesJson"
			| "transport"
			| "contractVersion"
			| "objectUploadJson"
			| "protocolSelectionPending"
		> &
			Partial<Pick<Session, "totalChunks">>,
		partNumbers: readonly number[],
		useRegionalEndpoint?: boolean
	): Promise<MultipartUrlRefreshBatch>;
}

/**
 * Normalized endpoint-family port for the migration seam. Existing callers use
 * MultipartApiPort (legacy by default); `/uploads` adapters use opaque registry
 * targets and never receive S3 coordinates from the shared engine.
 */
export interface FamilyAwareMultipartApiPort {
	readonly family: UploadEndpointFamily;
	complete(
		target: FamilyAwareMultipartUploadTarget,
		parts: readonly UploadCompletionPart[] | readonly UploadsCompletionPart[]
	): Promise<{ alreadyComplete: boolean }>;
	listParts(target: FamilyAwareMultipartUploadTarget): Promise<ListPartsEvidence>;
	refreshUrls(
		target: FamilyAwareMultipartUploadTarget,
		partNumbers: readonly number[],
		useRegionalEndpoint?: boolean
	): Promise<MultipartUrlRefreshBatch | UploadTargetEnvelope[]>;
}

export type LegacyMultipartApiPort<Session extends MultipartSessionShape> = MultipartApiPort<Session>;
export type UploadsMultipartApiPort = FamilyAwareMultipartApiPort & {
	readonly family: "uploads";
	complete(target: UploadsMultipartUploadTarget, parts: readonly UploadsCompletionPart[]): Promise<{ alreadyComplete: boolean }>;
	listParts(target: UploadsMultipartUploadTarget): Promise<ListPartsEvidence>;
	refreshUrls(target: UploadsMultipartUploadTarget, partNumbers: readonly number[]): Promise<UploadTargetEnvelope[]>;
};

export interface PersistedTargetInitiator<Session extends MultipartSessionShape, Target extends FamilyAwareMultipartUploadTarget> {
	initiatePersisted(request: { session: Session; stableId: string; totalChunks: number }): Promise<Target>;
	// Best-effort rollback for a target initiatePersisted just minted on S3 that the driver could not
	// go on to keep (a malformed response, a refused/rejected attach, a lost-ownership race) — the
	// identical "mint then might have to give it back" shape as UploadTargetInitiator's abortFile/
	// abortVideo, and required for the SAME reason: without it, the mint is a real, live S3 multipart
	// upload with nothing left to ever compensate it (round 38 / LSP-17735). Optional only so an
	// out-of-tree consumer of this generic package does not break on upgrade; every in-repo ports
	// implementation provides it.
	abortPersisted?(target: Target, options?: { preserveRecordingSession?: boolean }): Promise<void>;
	/**
	 * Reserve at pick (docs/plans/2026-10-16-web-lazy-multipart-initiate.md): tell the server about this
	 * durable session NOW — it records the pending upload row the wait-for-uploads gate and the "finish
	 * your upload" reminder count — without creating the S3 multipart upload. `initiatePersisted` for the
	 * same session later mints it. Implementations MUST send the same identity `initiatePersisted` sends
	 * for this `stableId`; the server finds the reserved row by it. Optional: a port without it simply
	 * initiates lazily with no reservation.
	 */
	reservePersisted?(request: { session: Session; stableId: string; totalChunks: number }): Promise<void>;
}

/**
 * The ONE identity rule for a persisted session's server-side upload: the same value is reserved at pick
 * and later initiated, so the server attaches the multipart upload to the row it reserved.
 */
export function persistedSessionStableId(session: Pick<MultipartSessionShape, "id" | "reconciledIdempotencyId" | "idempotencyId">): string {
	return session.reconciledIdempotencyId ?? session.idempotencyId ?? session.id;
}

interface UploadErrorClassifier {
	classifyStorage(error: unknown): "quota" | "internal" | "other";
	classifyCompletion(error: unknown): "lost" | "transient" | "fatal";
	normalize(error: unknown): Error;
	createMultipartTargetValidationError(failure: MultipartTargetValidationFailure, file: DriverFile): Error;
	createChunkSourceError(kind: ChunkSourceFailureKind, fileName: string): Error;
}

interface ChunkedUploadPolicies<Session> {
	readonly lostRecoveryPolicy: LostRecoveryPolicy;
	/**
	 * What to do when a chunk PUT proves the multipart upload no longer exists on S3
	 * (`MultipartUploadGoneError`). "probe-complete" asks the server to complete it anyway: an MPU
	 * that is gone because it was ALREADY assembled answers `alreadyComplete`, which retires the
	 * session as genuinely finished instead of re-uploading a file the server already holds.
	 *
	 * Defaults, when omitted, to "probe-complete" for a `fail-fast` lost policy and "none" otherwise —
	 * the exact behavior this gate had when it was keyed on `lostRecoveryPolicy` alone. It is split
	 * out so an app can adopt R-1's non-destructive `reinitialize` lost policy without also losing the
	 * gone-but-already-assembled probe, which is an orthogonal question.
	 */
	readonly goneMultipartRecovery?: "probe-complete" | "none";
	readonly directUploadQuotaPolicy: "continue-without-resume" | "fail";
	readonly terminalRecovery: ReconciliationTerminalPolicy<Session, DriverResumeOutcome>;
	readonly cancelEnabled: boolean;
	readonly probeAlreadyCompleteWithNoParts?: boolean;
}

export interface ChunkedUploadDriverPorts<
	FileLike extends DriverFile,
	Session extends MultipartSessionShape,
	LeaseToken,
	FailureKind extends ResumeFailureKind = ResumeFailureKind
> {
	createEphemeralSession(
		input: NewSessionInput & {
			ephemeral: true;
			target: FamilyAwareMultipartUploadTarget;
		}
	): Session & { ephemeral: true };
	sessionStore: ChunkedUploadSessionStore<Session, LeaseToken>;
	leaseCoordinator: UploadLeaseCoordinator<Session, LeaseToken>;
	chunkSources: ChunkSourceFactory<FileLike, Session>;
	chunkTransport: S3ChunkTransport;
	multipartApi: MultipartApiPort<Session>;
	persistedTargetInitiator: PersistedTargetInitiator<Session, FamilyAwareMultipartUploadTarget>;
	persistedResume: PersistedResumePort<Session, FailureKind>;
	policies: ChunkedUploadPolicies<Session>;
	errors: UploadErrorClassifier;
	telemetry: UploadTelemetry;
	connectivity?: {
		isOnline(): boolean;
		waitForOnline(signal: AbortSignal): Promise<void>;
	};
	isPresignedUrlExpired(url: string): boolean;
	retryDelay(attempt: number, reason: "retry" | "refresh"): Promise<void>;
	retryRandom?: () => number;
	createId(): string;
	now(): number;
	chunkConcurrency: number;
	maxChunkRetries: number;
	maxCloudArrayBufferBytes: number;
}

export interface ChunkedUploadDriver<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken> {
	persistFileToIndexedDB(input: {
		file: FileLike;
		target: FamilyAwareMultipartUploadTarget;
		conversationId: string;
		batchId?: string;
		uploadScope?: { organizationId?: string; userId?: string };
		fileCategory?: UploadFileCategory;
		projectChecklistItemId?: string;
		photoTime?: string;
		onPreparationProgress?: (chunksPersisted: number, totalChunks: number) => void;
		captureEvidence?: Record<string, unknown>;
		/** Content-sniffed MIME the caller already verified (e.g. magic-byte detection) — preferred
		 * over the FileLike's own (spoofable) reported type when writing the durable session record,
		 * so a background drain that later mints this session's target from IndexedDB alone doesn't
		 * re-derive the untrusted value (LSP-17735). */
		detectedMimeType?: string;
	}): Promise<string>;
	persistDeferredFileToIndexedDB(input: {
		file: FileLike;
		onSessionCreated?: (session: Session) => void | Promise<void>;
		onPreparationProgress?: (chunksPersisted: number, totalChunks: number) => void;
		conversationId: string;
		batchId?: string;
		uploadScope?: { organizationId?: string; userId?: string };
		fileCategory?: UploadFileCategory;
		projectChecklistItemId?: string;
		photoTime?: string;
		captureEvidence?: Record<string, unknown>;
		/** See persistFileToIndexedDB's identical parameter. */
		detectedMimeType?: string;
	}): Promise<string>;
	attachUploadTargetToDeferredSession(input: {
		sessionId: string;
		target: FamilyAwareMultipartUploadTarget;
		/** Compatibility token used by legacy facades that already own setup liveness. */
		leaseToken?: LeaseToken;
	}): Promise<DeferredTargetAttachmentOutcome>;
	uploadFile(input: {
		file: FileLike;
		target: FamilyAwareMultipartUploadTarget;
		conversationId?: string;
		/** Durable picker-batch identity when this direct upload is the pre-persist fallback. */
		batchId?: string;
		uploadScope?: { organizationId?: string; userId?: string };
		fileCategory?: UploadFileCategory;
		onProgress(bytesUploaded: number): void;
		cancelSignal?: AbortSignal;
		releaseActiveOnFinally?: boolean;
		leaseToken?: LeaseToken;
		/** See persistFileToIndexedDB's identical parameter (LSP-17735). */
		detectedMimeType?: string;
	}): Promise<DriverUploadOutcome>;
	resumeFileUpload(input: {
		session: Session;
		allChunks: DriverChunkMeta[];
		unuploadedChunks: DriverChunkMeta[];
		chunkUrls: string[];
		chunkSource?: ChunkSource;
		refreshPresignedUrl?: (chunkIndex: number) => Promise<string>;
		onProgress?: (bytesUploaded: number) => void;
		onActivity?: () => void;
		onChunkComplete?: (input: { chunkIndex: number; chunkSize: number; chunksCompleted: number; totalPendingChunks: number }) => void;
		createTruncatedPartsError?: (uploadedPartCount: number) => Error;
		cancelSignal?: AbortSignal;
		leaseScope?: UploadLeaseScope<Session, LeaseToken>;
		behavior?: {
			markPendingOnError?: boolean;
			skipLeaseManagement?: boolean;
			shouldTombstoneCancelled?: () => boolean;
			/** False for an old single-flight attempt that a replacement has superseded. */
			isSuperseded?: () => boolean;
		};
	}): Promise<"completed" | "yielded">;
	resumePersistedMultipartSession(input: {
		session: Session;
		onProgress?: (bytesUploaded: number) => void;
		onChunkComplete?: (input: { chunkIndex: number; chunkSize: number; chunksCompleted: number; totalPendingChunks: number }) => void;
		cancelSignal?: AbortSignal;
		leaseScope?: UploadLeaseScope<Session, LeaseToken>;
	}): Promise<DriverResumeOutcome>;
	finalizeMultipartCompletion(input: {
		session: Session;
		parts: { ETag: string; PartNumber: number }[];
		lease: UploadLease<Session>;
	}): Promise<boolean>;
	reinitializeSession(input: { session: Session; lease: UploadLease<Session> }): Promise<Session>;
	discardLostSession(input: { sessionId: string; uploadId: string }): Promise<void>;
}
